import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TRANSLATION_KEYS } from '@core/constants';
import {
  AdministrativeRuleHistoryModel,
  AdministrativeRulesFrecuencyModel,
  CollectionResponse,
  Frecuency,
  FrecuencyByCicleModel,
  PaginationModel,
  SingleItemResponse,
} from '@core/models';
import { Api } from '@core/services/api.state';
import { ApiService } from '@core/services/api/api.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import {
  DialogAction,
  DialogCloseResult,
  DialogService,
} from '@progress/kendo-angular-dialog';
import { Observable, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AdministrativeRuleHistoryService extends Api<AdministrativeRuleHistoryModel> {
  override endpoint: string;

  constructor(
    protected override readonly apiService: ApiService<AdministrativeRuleHistoryModel>,
    private http: HttpClient,
    private translateService: TranslateService,
    private dialogService: DialogService
  ) {
    super(apiService);
    this.endpoint = `${environment.apiUrl}/v1/admin/students/administrativeRules`;
  }
  public exportAdministrativeRuleHistory(
    params: PaginationModel
  ): Observable<Blob> {
    params.limit = 1000000;
    params.page = 1;
    const endpoint = `${this.endpoint}/export${`?${new URLSearchParams(
      params as any
    ).toString()}`}`;

    return this.http.get(endpoint, {
      responseType: 'blob',
    });
  }

  public updatePaymentFrecuency(
    data: AdministrativeRulesFrecuencyModel,
    isFromAdmin: boolean
  ): Observable<boolean> {
    const endpoint = `${environment.apiUrl}/v1/${
      isFromAdmin ? 'admin' : 'account'
    }/students/administrative-rules/${
      data.administrative_rule_id
    }/change-frequency`;
    return this.http.patch(endpoint, data).pipe(map(() => true));
  }

  public show(id: string): Observable<AdministrativeRuleHistoryModel> {
    const endpoint = `${environment.apiUrl}/v1/admin/students/administrative-rules/${id}`;
    return this.http
      .get<SingleItemResponse<AdministrativeRuleHistoryModel>>(endpoint)
      .pipe(map((response) => response.data));
  }

  public getPaymentFrecuency(
    studentCycleId: string,
    isFromAdmin: boolean
  ): Observable<Frecuency[]> {
    const endpoint = `${environment.apiUrl}/v1/${
      isFromAdmin ? 'admin' : 'account'
    }/students/getFrequencies${`?${new URLSearchParams({
      student_cycle_id: studentCycleId,
    } as any).toString()}`}`;
    return this.http
      .get<CollectionResponse<Frecuency>>(endpoint)
      .pipe(map((response) => response.data.items));
  }

  public getCurrentPaymentFrecuency(
    studentCycleId: string,
    schoolId: string,
    isFromAdmin: boolean
  ): Observable<FrecuencyByCicleModel[]> {
    const endpoint = `${environment.apiUrl}/v1/${
      isFromAdmin ? 'admin' : 'account'
    }/students/getFrequencyConfiguration${`?${new URLSearchParams({
      student_cycle_id: studentCycleId,
      school_id: schoolId,
    } as any).toString()}`}`;
    return this.http
      .get<CollectionResponse<FrecuencyByCicleModel>>(endpoint)
      .pipe(
        map((response) => {
          return response.data.items;
        })
      );
  }

  public regenerate(id: string) {
    return this.dialogService
      .open({
        title: 'Regenerar Acuse de Recibo',
        content:
          '¿Está seguro de que desea Regenerar el Acuse de Recibo? Esta acción no se puede deshacer.',
        actions: [
          {
            text: this.translateService.instant(
              TRANSLATION_KEYS.BUTTONS.CANCEL_LABEL
            ),
          },
          { text: 'Confirmar', themeColor: 'primary' },
        ],
        actionsLayout: 'end',
        width: 500,
        height: 248,
        cssClass: 'confirm-dialog',
      })
      .result.pipe(
        switchMap((result) => {
          if (
            !(result instanceof DialogCloseResult) &&
            (result as DialogAction).text === 'Confirmar'
          ) {
            return this.apiService.post(
              `${environment.apiUrl}/v1/admin/students/regenerate/receipt`,
              JSON.stringify({ admin_rules_ids: [id] })
            );
          }
          return of(null);
        })
      );
  }

  public updateAdministrativeRulesSignature(
    data: AdministrativeRulesFrecuencyModel
  ): Observable<boolean> {
    const endpoint = `${environment.apiUrl}/v1/account/administrative-rules/${data.administrative_rule_id}`;
    return this.http.patch(endpoint, data).pipe(map(() => true));
  }
}
